"use client";

import { orgHelpCenterBase } from "@/lib/help-center-seo";
import {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
} from "react";
import {
  type HelpSpaTarget,
  helpSpaHref,
  helpSpaHrefFromBase,
} from "./help-spa-path";

/** Null = public `/help/{org}` links; the portal sets its own base. */
const HelpLinkBaseContext = createContext<string | null>(null);

/** Where the public help center is served: canonical base, docs host or not. */
export type HelpSite = {
  readonly canonical_base: string;
  /** Docs host: links at the host root, sign-in on the app origin. */
  readonly docs: { readonly app_origin: string } | null;
};

const HelpSiteContext = createContext<HelpSite | null>(null);

export function HelpLinkBaseProvider({
  base,
  children,
}: {
  readonly base: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <HelpLinkBaseContext.Provider value={base}>
      {children}
    </HelpLinkBaseContext.Provider>
  );
}

/** Public help center site; the docs host roots every link at `/`. */
export function HelpSiteProvider({
  site,
  children,
}: {
  readonly site: HelpSite;
  readonly children: ReactNode;
}): ReactElement {
  const inner = (
    <HelpSiteContext.Provider value={site}>{children}</HelpSiteContext.Provider>
  );
  return site.docs === null ? (
    inner
  ) : (
    <HelpLinkBaseProvider base="">{inner}</HelpLinkBaseProvider>
  );
}

/** Link builder for help targets; same components serve `/help` and the portal. */
export function useHelpHref(
  orgSlug: string,
): (target: HelpSpaTarget) => string {
  const base = useContext(HelpLinkBaseContext);
  return useCallback(
    (target: HelpSpaTarget): string =>
      base === null
        ? helpSpaHref(orgSlug, target)
        : helpSpaHrefFromBase(base, target),
    [base, orgSlug],
  );
}

/** Absolute base for JSON-LD; outside a site provider the org host. */
export function useHelpCanonicalBase(orgSlug: string): string {
  return useContext(HelpSiteContext)?.canonical_base ?? orgHelpCenterBase(orgSlug);
}

/** Prefix for app links (sign-in, dashboard): empty = same host. */
export function useHelpAppOrigin(): string {
  return useContext(HelpSiteContext)?.docs?.app_origin ?? "";
}
